/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LogProfilesPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main log profiles management page with data grid, CRUD operations,
    and comprehensive audit logging configuration.

 ?? Notes:
    - Integrates DataGridSimple for log profile listing and management
    - Supports log profile creation, editing, deletion, and duplication
    - Implements search and filtering functionality
    - Uses confirmation modals for destructive actions
    - Integrates with permissions system for access control

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNavigationStore } from '../../store/navigation';
import { FileText } from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModulePageContainer } from '../../components/layouts';
import { useLogProfilesList } from './hooks';
import { useLogProfilesColumnRenderers, LogProfilesActions } from './components';
import { deleteLogProfile, duplicateLogProfile } from './api';
import { LogProfile } from './types';

export default function LogProfilesPage() {
  const { t, refreshKey } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<LogProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Force re-render when language changes
  }, [refreshKey]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => {
      setCurrentPageTitle(null);
    };
  }, [setCurrentPageTitle]);

  const {
    data: profiles,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useLogProfilesList();

  const columnRenderers = useLogProfilesColumnRenderers();
  const tenantSlug = user?.tenant?.slug || t('logProfiles.defaultTenant');

  const handleCreateProfile = useCallback(() => {
    navigate('/log-profiles/new');
  }, [navigate]);

  const handleEditProfile = useCallback(
    (profile: LogProfile) => {
      navigate(`/log-profiles/${profile.uuid ?? profile.id}`);
    },
    [navigate]
  );

  const handleDuplicateProfile = useCallback(
    async (profile: LogProfile) => {
      try {
        const duplicated = await duplicateLogProfile(profile.id, tenantSlug, user?.id);
        showSuccess(t('logProfiles.messages.duplicateSuccess', { name: duplicated.name }));
        refresh();
      } catch (error) {
        showError(error instanceof Error ? error.message : t('logProfiles.messages.duplicateError'));
      }
    },
    [navigate, tenantSlug, user?.id, showSuccess, showError, t, refresh]
  );

  const handleDeleteProfile = useCallback((profile: LogProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!profileToDelete) return;

    if (profileToDelete.usersCount > 0) {
      showError(t('logProfiles.messages.deleteErrorUsersAssigned'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      return;
    }

    try {
      setIsDeleting(true);
      await deleteLogProfile(profileToDelete.id, tenantSlug, user?.id);
      showSuccess(t('logProfiles.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setProfileToDelete(null);
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('logProfiles.messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, showError, showSuccess, t, tenantSlug, user?.id, refresh]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setProfileToDelete(null);
    setIsDeleting(false);
  }, []);


  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            titleIcon={<FileText className="h-5 w-5 text-primary" />}
            title={t('logProfiles.title')}
            data={profiles}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columns={columns.map((col) => ({
              ...col,
              render: columnRenderers[col.key as keyof typeof columnRenderers],
            }))}
            onColumnToggle={handleColumnToggle}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreateProfile}
            onEdit={handleEditProfile}
            onDuplicate={handleDuplicateProfile}
            onDelete={handleDeleteProfile}
            addButtonText={t('common.add')}
            searchPlaceholder={t('logProfiles.search')}
            emptyMessage={t('logProfiles.empty')}
            deleteMessage={(item: LogProfile) =>
              item?.name
                ? t('logProfiles.confirmDelete.named', { name: item.name })
                : t('logProfiles.confirmDelete.generic')
            }
            renderActions={(item) => (
              <LogProfilesActions
                item={item as LogProfile}
                onEdit={handleEditProfile}
                onDuplicate={handleDuplicateProfile}
                onDelete={handleDeleteProfile}
              />
            )}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={cancelDelete}
            onConfirm={confirmDelete}
            title={t('logProfiles.delete.title')}
            message={
              profileToDelete?.name
                ? t('logProfiles.confirmDelete.named', { name: profileToDelete.name })
                : t('logProfiles.confirmDelete.generic')
            }
            confirmText={t('logProfiles.delete.confirm')}
            cancelText={t('common.cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}
